import { retrieveRawInitData } from '@tma.js/sdk';
import type { WebApp } from '@twa-dev/types';

export type TelegramUser = {
  telegramId: string;
  first_name: string;
  last_name: string;
  username?: string;
  photo_url?: string;
  role?: 'user' | 'organizer' | 'admin';
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: WebApp;
    };
  }
}

export function getTelegramWebApp(): WebApp | undefined {
  return window.Telegram?.WebApp;
}

// Cache initData so it survives URL changes after React Router navigation.
let cachedInitData: string | null = null;

/** Manually seed the cache (used by bootstrap when initData comes from a mock or external source). */
export function setTelegramInitData(value: string): void {
  cachedInitData = value;
}

export function getTelegramInitData(): string | null {
  if (cachedInitData) return cachedInitData;

  // Recommended way for Mini Apps: parse launch params (tgWebAppData).
  try {
    const raw = retrieveRawInitData();
    if (raw && raw.trim()) {
      cachedInitData = raw;
      return cachedInitData;
    }
  } catch {
    // Ignore if not in Telegram launch context.
  }

  const direct = getTelegramWebApp()?.initData;
  if (direct && direct.trim()) {
    cachedInitData = direct;
    return cachedInitData;
  }

  // In some launch modes Telegram passes initData via URL params (e.g. external browser / deep links).
  // Typical formats:
  // - https://host/?tgWebAppData=...&tgWebAppVersion=...
  // - https://host/#tgWebAppData=...&tgWebAppVersion=...
  const fromSearch = new URLSearchParams(window.location.search).get('tgWebAppData');
  if (fromSearch && fromSearch.trim()) {
    cachedInitData = fromSearch;
    return cachedInitData;
  }

  const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const fromHash = new URLSearchParams(hash ?? '').get('tgWebAppData');
  if (fromHash && fromHash.trim()) {
    cachedInitData = fromHash;
    return cachedInitData;
  }

  return null;
}

/** True when the app runs inside Telegram (initData available); false in a regular browser. */
export function isTelegramEnvironment(): boolean {
  return getTelegramInitData() !== null;
}

export function openTelegramProfileLink(user: {
  telegramId: string;
  username?: string;
}): void {
  const username = user.username?.trim();
  const url = username ? `https://t.me/${username}` : `tg://user?id=${encodeURIComponent(user.telegramId)}`;

  openTelegramLink(url);
}

export function openTelegramLink(url: string): void {
  const trimmed = url.trim();
  if (!trimmed) return;

  const webApp = getTelegramWebApp();
  if (webApp?.openTelegramLink && trimmed.startsWith('https://t.me/')) {
    webApp.openTelegramLink(trimmed);
    return;
  }

  window.open(trimmed, '_blank', 'noopener,noreferrer');
}

export function openExternalLink(url: string): void {
  const trimmed = url.trim();
  if (!trimmed) return;

  if (trimmed.startsWith('https://t.me/')) {
    openTelegramLink(trimmed);
    return;
  }

  const webApp = getTelegramWebApp();
  if (webApp?.openLink) {
    webApp.openLink(trimmed);
    return;
  }

  window.open(trimmed, '_blank', 'noopener,noreferrer');
}

export function applyTelegramThemeToDocument(): void {
  // Telegram theme vars are exposed as CSS variables; we rely on them in CSS.
  // This function exists to optionally trigger ready/expand.
  const webApp = getTelegramWebApp();
  webApp?.ready();
  webApp?.expand();
}

/**
 * Set up a listener that calls the provided callback when the Mini App is closing.
 * This is triggered by:
 * - User swiping down to close
 * - User pressing the close button
 * - App being backgrounded (visibility hidden)
 *
 * Note: BackButton is NOT used here as it's meant for in-app navigation.
 *
 * @param onClose - Callback to execute when app is closing
 * @returns Cleanup function to remove the listeners
 */
export function onMiniAppClose(onClose: () => void): () => void {
  // Handle page hide (fired when page is being unloaded/closed)
  const handlePageHide = () => {
    onClose();
  };

  // Handle visibility change (app goes to background or is hidden)
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      onClose();
    }
  };

  window.addEventListener('pagehide', handlePageHide);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}
